import type { ZodType, ZodIssue } from 'zod';

/**
 * Parse a response strictly, but let a bad ROW cost a row rather than a screen.
 *
 * The rule this file encodes, and the reason it exists at all:
 *
 *   **The envelope must parse. The rows may degrade.**
 *
 * Strict validation is the thing that catches the dashboard and the servers
 * drifting apart, and it has caught real drift more than once, so it is not
 * being loosened. What was wrong was the blast radius. A single row with an
 * unexpected field rejected the whole response, and the screen rendered "the
 * API returned an unexpected shape" instead of the other ninety nine rows it
 * could have drawn. One session with no identity took out the entire Sessions
 * page.
 *
 * That trade is backwards in both directions. The reader loses everything to
 * protect them from one row, and the defect is reported as a wall rather than
 * as the small, specific thing it is.
 *
 * So: if every complaint Zod makes is located INSIDE an array element, those
 * elements are removed and the response is parsed again. Anything else, a
 * missing `meta`, a scalar of the wrong type, a renamed top level field, still
 * throws, because a screen that cannot tell what it is looking at should say so
 * rather than draw a confident empty state.
 *
 * Dropping is never silent. Every drop is reported to the caller, which records
 * it and shows it, because a list quietly one row short is a worse bug than a
 * loud one: it looks like data.
 */

/** One array element that failed to parse and was removed. */
export interface DroppedElement {
  /** Where the element lived, e.g. `results.recent_sess.rows`. */
  readonly container: string;
  /** Its index within that array. */
  readonly index: number;
  /** Zod's complaint, e.g. `distinct_id Invalid input`. */
  readonly detail: string;
}

export interface TolerantResult<T> {
  readonly data: T;
  readonly dropped: readonly DroppedElement[];
}

/**
 * How many prune-and-retry passes are allowed.
 *
 * Zod reports every issue it finds in one pass, so one is normally enough. A
 * union or a refinement can hide later issues behind earlier ones, hence a
 * couple more. Bounded rather than looped to exhaustion because a schema that
 * keeps producing new failures is drift, not a stray row, and the honest answer
 * to that is the error.
 */
const MAX_PASSES = 3;

/**
 * The deepest array element on an issue path, or null when the issue is not
 * inside one.
 *
 * Zod uses numbers for array indices and strings for object keys, so a numeric
 * segment is an array index. The DEEPEST one is chosen so the smallest possible
 * thing is removed: for `results.sessions.rows.10.distinct_id` that is
 * `rows[10]`, not the whole `sessions` result.
 */
function deepestElement(
  path: readonly PropertyKey[],
): { container: PropertyKey[]; index: number } | null {
  for (let at = path.length - 1; at >= 0; at -= 1) {
    const segment = path[at];
    if (typeof segment === 'number') {
      return { container: path.slice(0, at) as PropertyKey[], index: segment };
    }
  }
  return null;
}

function describe(issue: ZodIssue): string {
  const tail = issue.path.slice(deepestElementDepth(issue.path));
  const where = tail.join('.');
  return where === '' ? issue.message : `${where} ${issue.message}`;
}

function deepestElementDepth(path: readonly PropertyKey[]): number {
  for (let at = path.length - 1; at >= 0; at -= 1) {
    if (typeof path[at] === 'number') return at + 1;
  }
  return 0;
}

function readPath(root: unknown, path: readonly PropertyKey[]): unknown {
  let cursor: unknown = root;
  for (const segment of path) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<PropertyKey, unknown>)[segment];
  }
  return cursor;
}

/**
 * Remove the named elements from the arrays they live in.
 *
 * Mutates a structural copy rather than the caller's body, and removes highest
 * index first so earlier removals cannot shift later ones.
 */
function prune(body: unknown, drops: readonly DropTarget[]): unknown {
  const copy: unknown = structuredClone(body);
  const byContainer = new Map<string, { container: PropertyKey[]; indices: number[] }>();
  for (const drop of drops) {
    const key = JSON.stringify(drop.container);
    const existing = byContainer.get(key);
    if (existing) existing.indices.push(drop.index);
    else byContainer.set(key, { container: drop.container, indices: [drop.index] });
  }
  for (const { container, indices } of byContainer.values()) {
    const target = readPath(copy, container);
    if (!Array.isArray(target)) continue;
    for (const index of [...new Set(indices)].sort((a, b) => b - a)) {
      target.splice(index, 1);
    }
  }
  return copy;
}

interface DropTarget {
  container: PropertyKey[];
  index: number;
  detail: string;
}

/**
 * @throws the schema's own error when the failure is not confined to array
 *   elements, or when pruning cannot make the response valid.
 */
export function parseTolerant<T>(schema: ZodType<T>, body: unknown): TolerantResult<T> {
  let candidate = body;
  const dropped: DroppedElement[] = [];

  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    const parsed = schema.safeParse(candidate);
    if (parsed.success) return { data: parsed.data, dropped };

    const targets: DropTarget[] = [];
    for (const issue of parsed.error.issues) {
      const element = deepestElement(issue.path);
      // One complaint outside an array element makes the whole response
      // untrustworthy, so nothing is pruned and the caller sees the error.
      if (element === null) throw parsed.error;
      targets.push({ ...element, detail: describe(issue) });
    }
    if (targets.length === 0) throw parsed.error;

    candidate = prune(candidate, targets);

    // One entry per REMOVED ELEMENT, not per complaint. A row missing three
    // fields produces three issues and is still one row: counting issues would
    // report "6 unreadable" for two bad rows, which overstates the damage in
    // the one place a reader is trying to judge how much to trust the screen.
    const byElement = new Map<string, DroppedElement>();
    for (const target of targets) {
      const container = target.container.join('.');
      const key = `${container}[${String(target.index)}]`;
      const existing = byElement.get(key);
      if (existing === undefined) {
        byElement.set(key, { container, index: target.index, detail: target.detail });
      } else if (!existing.detail.includes(target.detail)) {
        byElement.set(key, {
          ...existing,
          detail: `${existing.detail}, ${target.detail}`,
        });
      }
    }
    dropped.push(...byElement.values());
  }

  // Still failing after pruning everything it complained about. That is drift,
  // not a stray row.
  return { data: schema.parse(candidate), dropped };
}
